import React, { useId, useState } from "react";
import classNames from "classnames";
import Select from "react-select";
import { useParams, useSearchParams } from "react-router-dom";
import { sortBy } from "lodash";
import { Switch } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import Loading from "@components/shared/Loading";
import LoadFailed, { showsOwnFailure } from "@components/zenith/ui/LoadFailed";
import Table from "@components/zenith/ui/Table";
import { useAppContext } from "@context/AppContext";
import { useAllExtractionRights } from "@hooks/query/zenith/useAllExtractionRights";
import { formatVolume } from "@utils/formatVolume";
import { formatSchemeDay } from "@utils/schemeDay";
import { extractionRightTypes } from "@services/extractionRight";
import EntitlementDetailDialog, {
  EntitlementStatusTag,
} from "./EntitlementDetailDialog";

const WaterEntitlements: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const captionId = useId();
  const { loggedInInfo } = useAppContext();
  const { id: level0ResourceId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const [showInactiveExtractionRights, setShowInactiveExtractionRights] =
    useState(searchParams.get("isActive") === "false");
  const [showDeletedExtractionRights, setShowDeletedExtractionRights] =
    useState(searchParams.get("isDeleted") === "false");
  const [selected, setSelected] = useState<any>();
  const [filter, setFilter] = useState<{
    extractionRightName: string;
    extractionRightType?: number;
  }>({
    extractionRightName: searchParams.get("name") ?? "",
    extractionRightType: searchParams.get("type")
      ? Number(searchParams.get("type"))
      : undefined,
  });

  const typeOptions = [
    { label: t("extraction_right.wa"), value: extractionRightTypes.WA },
    { label: t("extraction_right.wsa"), value: extractionRightTypes.WSA },
    {
      label: t("approval.stock_and_domestic.title"),
      value: extractionRightTypes.SD,
    },
    { label: t("extraction_right.zac"), value: extractionRightTypes.ZAC },
    { label: t("extraction_right.tws"), value: extractionRightTypes.TWS },
  ];

  const { walletId, subscriberId } = loggedInInfo?.userDetails ?? {};

  const {
    data: extractionRights = [],
    isLoading: isExtractionRightsLoading,
    isError,
  } = useAllExtractionRights({
    params: {
      assertedByWalletId: walletId,
      level0ResourceId: level0ResourceId,
      isActive:
        !showInactiveExtractionRights && !showDeletedExtractionRights
          ? true
          : false,
      isDeleted: !showDeletedExtractionRights ? false : true,
    },
    enabled: Boolean(walletId) && Boolean(level0ResourceId),
    ...showsOwnFailure,
  });

  const filteredExtractionRights = sortBy(
    extractionRights
      ?.filter(
        (right: any) =>
          right.type?.identifier !== "WH" &&
          right.type?.identifier !== "SA" &&
          !/^PT-Q/.test(right.name) &&
          !right.name.includes(`Quasi-Q/${subscriberId}`),
      )
      ?.filter(
        (right: any) =>
          filter.extractionRightName === "" ||
          right.name
            .toLowerCase()
            .includes(filter.extractionRightName.toLowerCase()),
      )
      ?.filter(
        (right: any) =>
          !filter.extractionRightType ||
          right.extractionRightTypeId === filter.extractionRightType,
      ),
    "name",
  );

  if (isError) return <LoadFailed />;
  if (isExtractionRightsLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-1 flex-wrap gap-3">
          <div className="min-w-56 max-w-sm flex-1">
            <Label htmlFor="extraction_right_name">
              {t("extraction_right.filter_extraction_right_number")}
            </Label>
            <SearchInput
              id="extraction_right_name"
              onChange={e =>
                setFilter(prev => ({
                  ...prev,
                  extractionRightName: e.target.value,
                }))
              }
              placeholder={t("common.search") as string}
              value={filter.extractionRightName || ""}
            />
          </div>
          <div className="w-64">
            <Label htmlFor="extraction_right_type">
              {t("extraction_right.filter_extraction_right_type")}
            </Label>
            <Select
              inputId="extraction_right_type"
              options={typeOptions}
              value={
                typeOptions.find(i => i.value === filter.extractionRightType) ??
                null
              }
              onChange={e =>
                setFilter(prev => ({
                  ...prev,
                  extractionRightType: e?.value,
                }))
              }
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 20 }) }}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <LabelledSwitch
            checked={showInactiveExtractionRights}
            onChange={setShowInactiveExtractionRights}
            label={t("extraction_right.show_inactivated_extraction_rights")}
          />
          <LabelledSwitch
            checked={showDeletedExtractionRights}
            onChange={setShowDeletedExtractionRights}
            label={t("extraction_right.show_deleted_extraction_rights")}
          />
        </div>
      </div>

      <p id={captionId} className="text-sm text-gray-500">
        {t("zenith.zone_accounts.entitlements_caption")}
      </p>
      <Table
        aria-labelledby={captionId}
        rowKey={(right: any) => right.id}
        rows={filteredExtractionRights}
        empty={t("extraction_right.no_data")}
        columns={[
          {
            key: "name",
            label: t("zenith.zone_accounts.reference"),
            render: (right: any) => (
              <button
                type="button"
                onClick={() => setSelected(right)}
                className="font-semibold text-primary-1 hover:underline"
              >
                {right.name}
              </button>
            ),
          },
          {
            key: "waterClass",
            label: t("water_class.water_class_header"),
            render: (right: any) => right.waterClass?.name,
          },
          {
            key: "volume",
            label: t("common.volume"),
            render: (right: any) => formatVolume(right.volume),
          },
          {
            key: "status",
            label: t("common.status"),
            render: (right: any) => <EntitlementStatusTag right={right} />,
          },
          {
            key: "activedAt",
            label: t("zenith.zone_accounts.issued"),
            render: (right: any) =>
              right.activedAt ? formatSchemeDay(right.activedAt) : "-",
          },
        ]}
      />
      {selected ? (
        <EntitlementDetailDialog
          right={selected}
          onClose={() => setSelected(undefined)}
        />
      ) : null}
    </div>
  );
};

const LabelledSwitch: React.FunctionComponent<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <Switch.Group as="div" className="flex items-center gap-2">
    <Switch
      checked={checked}
      onChange={onChange}
      className={classNames(
        "relative inline-flex h-6 w-11 items-center rounded-full",
        checked ? "bg-primary-1" : "bg-gray-200",
      )}
    >
      <span
        className={classNames(
          "inline-block h-4 w-4 transform rounded-full bg-white transition",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </Switch>
    <Switch.Label className="text-sm">{label}</Switch.Label>
  </Switch.Group>
);

export default WaterEntitlements;
